import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  SystemApp, Permission, Role, User, Group, SessionDto, SecuritySettings, PasswordPolicy, LoginAttempt,
  UserListItem, UserDetails, CreateUserRequest, UpdateUserRequest, AuditLog, UserSession, PagedResult,
  EmployeeListItem, BranchListItem, BranchDto, InsuranceCompanyListItem
  , ExternalEntityListItem, SystemOwnerSystem, SystemOwnerSystemDetails
} from './models';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private http = inject(HttpClient);
  private base = environment.apiBase;

  // Systems
  listSystems() { return this.http.get<SystemApp[]>(`${this.base}/api/systemapps`); }
  listMySystems() { return this.http.get<SystemApp[]>(`${this.base}/api/systemapps`, { params: { mine: 'true' } }); }
  createSystem(body: SystemApp) { return this.http.post<SystemApp>(`${this.base}/api/systemapps`, body); }
  updateSystem(id: string, body: SystemApp) { return this.http.put(`${this.base}/api/systemapps/${id}`, body); }
  deleteSystem(id: string) { return this.http.delete(`${this.base}/api/systemapps/${id}`); }

  // Permissions (Actions)
  listPermissions(q?: { systemKey?: string; search?: string; page?: number; pageSize?: number }) {
    let params = new HttpParams();
    if (q?.systemKey) params = params.set('systemKey', q.systemKey);
    if (q?.search) params = params.set('search', q.search);
    if (q?.page) params = params.set('page', q.page.toString());
    if (q?.pageSize) params = params.set('pageSize', q.pageSize.toString());
    return this.http.get<any>(`${this.base}/api/actions`, { params });
  }

  /**
   * Fetches the full permissions catalog by paging until all items are received.
   * Guards against backend pageSize caps that would otherwise return only a slice.
   */
  async listAllPermissions(filter?: { systemKey?: string; search?: string }): Promise<Permission[]> {
    const pageSize = 1000;
    let page = 1;
    const out: Permission[] = [];
    while (true) {
      const result: any = await this.listPermissions({
        ...(filter ?? {}),
        page,
        pageSize,
      }).toPromise();

      const items: Permission[] =
        result?.items ?? (Array.isArray(result) ? result : []);
      out.push(...items);

      const total: number | undefined = result?.total;
      if (typeof total === 'number') {
        if (out.length >= total || items.length === 0) break;
      } else {
        if (items.length < pageSize) break;
      }
      page++;
      if (page > 100) break; // hard safety
    }
    return out;
  }
  createPermission(body: Permission) { return this.http.post<Permission>(`${this.base}/api/actions`, body); }
  deletePermission(id: string) { return this.http.delete(`${this.base}/api/actions/${id}`); }

  // Roles
  listRoles() { return this.http.get<Role[]>(`${this.base}/api/roles`); }
  createRole(body: Role) { return this.http.post<Role>(`${this.base}/api/roles`, body); }
  updateRole(id: string, body: Role) { return this.http.put(`${this.base}/api/roles/${id}`, body); }
  deleteRole(id: string) { return this.http.delete(`${this.base}/api/roles/${id}`); }
  setRoleActions(id: string, actions: string[]) { return this.http.post(`${this.base}/api/roles/${id}/actions`, { actions }); }

  // Users - Updated to match backend API
  listUsers(params?: {
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    search?: string;
    status?: string;
    roleIds?: string;
    groupIds?: string;
    dateFrom?: string;
    dateTo?: string;
    lastLoginFrom?: string;
    lastLoginTo?: string;
    /** يطابق فلتر الخادم: وصول نظام أو صلاحيات دور على نفس المفتاح (مثل insurance) */
    portalSystemKey?: string;
    insuranceUsersOnly?: boolean;
    excludeInsuranceUsers?: boolean;
    excludePortalUsers?: boolean;
    includeDeleted?: boolean;
  }) {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<PagedResult<UserListItem>>(`${this.base}/api/users`, { params: httpParams });
  }

  getUser(id: string) {
    return this.http.get<UserDetails>(`${this.base}/api/users/${id}`);
  }

  createUser(body: CreateUserRequest) {
    return this.http.post<UserDetails>(`${this.base}/api/users`, body);
  }

  updateUser(id: string, body: UpdateUserRequest) {
    return this.http.put(`${this.base}/api/users/${id}`, body);
  }

  deleteUser(id: string) {
    return this.http.delete(`${this.base}/api/users/${id}`);
  }

  restoreUser(id: string) {
    return this.http.post(`${this.base}/api/users/${id}/restore`, {});
  }

  setUserStatus(id: string, isActive: boolean) {
    return this.http.patch(`${this.base}/api/users/${id}/status`, { isActive });
  }

  setUserLock(id: string, lock: boolean) {
    return this.http.patch(`${this.base}/api/users/${id}/lock`, { lock, unlock: !lock });
  }

  adminResetPassword(id: string, newPassword: string) {
    return this.http.post(`${this.base}/api/users/${id}/password/reset`, { newPassword });
  }

  setUserRoles(id: string, roleIds: string[]) {
    return this.http.post(`${this.base}/api/users/${id}/roles`, { roleIds });
  }

  setUserGroups(id: string, groupIds: string[]) {
    return this.http.post(`${this.base}/api/users/${id}/groups`, { groupIds });
  }

  getUserAuditLogs(id: string, params?: {
    page?: number;
    pageSize?: number;
    actionType?: string;
    from?: string;
    to?: string;
  }) {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<PagedResult<AuditLog>>(`${this.base}/api/users/${id}/audit`, { params: httpParams });
  }

  getUserSessions(id: string) {
    return this.http.get<UserSession[]>(`${this.base}/api/users/${id}/sessions`);
  }

  revokeUserSessions(id: string, sessionIds?: string[], revokeAll?: boolean) {
    return this.http.post(`${this.base}/api/users/${id}/sessions/revoke`, { sessionIds, revokeAll });
  }

  // Groups
  listGroups() { return this.http.get<Group[]>(`${this.base}/api/groups`); }
  createGroup(body: Group) { return this.http.post<Group>(`${this.base}/api/groups`, body); }
  updateGroup(id: string, body: Group) { return this.http.put(`${this.base}/api/groups/${id}`, body); }
  deleteGroup(id: string) { return this.http.delete(`${this.base}/api/groups/${id}`); }
  getGroupUsers(id: string) { return this.http.get<string[]>(`${this.base}/api/groups/${id}/users`); }
  getGroupRoles(id: string) { return this.http.get<string[]>(`${this.base}/api/groups/${id}/roles`); }
  setGroupUsers(id: string, userIds: string[]) { return this.http.post(`${this.base}/api/groups/${id}/users`, { userIds }); }
  setGroupRoles(id: string, roleIds: string[]) { return this.http.post(`${this.base}/api/groups/${id}/roles`, { roleIds }); }

  // Sessions (Admin) - legacy endpoints
  listUserSessionsOld(userId: string) { return this.http.get<SessionDto[]>(`${this.base}/api/sessions/${userId}/active`); }
  revokeUserSession(userId: string, sessionId: string) { return this.http.post(`${this.base}/api/sessions/${userId}/revoke`, { sessionId }); }
  revokeAllUserSessionsOld(userId: string) { return this.http.post(`${this.base}/api/sessions/${userId}/revoke-all`, {}); }
  evictOldest(userId: string) { return this.http.post<{ evictedSessionId: string }>(`${this.base}/api/sessions/${userId}/evict-oldest`, {}); }

  // Settings
  getSecurity() { return this.http.get<SecuritySettings>(`${this.base}/api/settings/security`); }
  saveSecurity(body: SecuritySettings) { return this.http.put(`${this.base}/api/settings/security`, body); }
  getPasswordPolicy() { return this.http.get<PasswordPolicy>(`${this.base}/api/settings/password-policy`); }
  savePasswordPolicy(body: PasswordPolicy) { return this.http.put(`${this.base}/api/settings/password-policy`, body); }

  // Login Attempts
  listLoginAttempts(q?: { user?: string; succeeded?: boolean | string; from?: string; to?: string; page?: number; pageSize?: number }) {
    let p = new HttpParams();
    if (q) {
      Object.keys(q).forEach(key => {
        const value = (q as any)[key];
        if (value !== undefined && value !== null && value !== '') {
          p = p.set(key, value.toString());
        }
      });
    }
    return this.http.get<any>(`${this.base}/api/audit/login-attempts`, { params: p });
  }

  // HR - Employees
  listEmployees() {
    return this.http.get<EmployeeListItem[]>(`${this.base}/api/hr/employees`);
  }

  // HR - Branches
  listBranches() {
    return this.http.get<BranchListItem[]>(`${this.base}/api/hr/branches`);
  }

  /** يستدعي المختبر ثم يعيد قائمة المرآة — يُفضّل استدعاؤه عند دخول شاشة مستخدمي التأمين. */
  importInsuranceCompaniesFromLab() {
    return this.http.post<{ upserted: number; pagesFetched: number; warning?: string | null }>(
      `${this.base}/api/lab/insurance-companies/import-from-lab`,
      {}
    );
  }

  listInsuranceCompanies() {
    return this.http.get<InsuranceCompanyListItem[]>(`${this.base}/api/lab/insurance-companies`);
  }

  importPatientsFromLab() {
    return this.http.post<{ upserted: number; pagesFetched: number; warning?: string | null }>(
      `${this.base}/api/lab/patients/import-from-lab`,
      {}
    );
  }

  importDoctorsFromLab() {
    return this.http.post<{ upserted: number; pagesFetched: number; warning?: string | null }>(
      `${this.base}/api/lab/doctors/import-from-lab`,
      {}
    );
  }

  importReferralLabsFromLab() {
    return this.http.post<{ upserted: number; pagesFetched: number; warning?: string | null }>(
      `${this.base}/api/lab/referral-labs/import-from-lab`,
      {}
    );
  }

  listPatients() {
    return this.http.get<ExternalEntityListItem[]>(`${this.base}/api/lab/patients`);
  }

  listDoctors() {
    return this.http.get<ExternalEntityListItem[]>(`${this.base}/api/lab/doctors`);
  }

  listReferralLabs() {
    return this.http.get<ExternalEntityListItem[]>(`${this.base}/api/lab/referral-labs`);
  }

  // User - Employee Link
  linkUserEmployee(userId: string, employeeId: number | null) {
    return this.http.patch(`${this.base}/api/users/${userId}/employee`, { employeeId });
  }

  // User - Branches
  setUserBranches(userId: string, branchIds: number[]) {
    return this.http.post(`${this.base}/api/users/${userId}/branches`, { branchIds });
  }

  getUserBranches(userId: string) {
    return this.http.get<BranchDto[]>(`${this.base}/api/users/${userId}/branches`);
  }

  // System Owner
  listSystemOwnerSystems() {
    return this.http.get<SystemOwnerSystem[]>(`${this.base}/api/system-owner/systems`);
  }

  getSystemOwnerSystem(systemKey: string) {
    return this.http.get<SystemOwnerSystemDetails>(
      `${this.base}/api/system-owner/systems/${encodeURIComponent(systemKey)}`
    );
  }

  addSystemSuperAdmin(systemKey: string, userId: string) {
    return this.http.post(
      `${this.base}/api/system-owner/systems/${encodeURIComponent(systemKey)}/super-admins`,
      { userId }
    );
  }

  removeSystemSuperAdmin(systemKey: string, userId: string) {
    return this.http.delete(
      `${this.base}/api/system-owner/systems/${encodeURIComponent(systemKey)}/super-admins/${userId}`
    );
  }
}
